import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { uploadPublicAsset } from "@/lib/upload";
import { StepTitle, OField, inputCls, NavButtons, money } from "@/components/onboard/ui";
import { Eraser } from "@phosphor-icons/react";
import { toast } from "sonner";

function SignaturePad({ padRef, onChange }) {
  const drawing = useRef(false);
  useEffect(() => {
    const c = padRef.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio; c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio); ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
  }, [padRef]);
  const pos = (e) => { const r = padRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { drawing.current = true; const ctx = padRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); padRef.current.setPointerCapture(e.pointerId); };
  const move = (e) => { if (!drawing.current) return; const ctx = padRef.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); onChange(true); };
  const up = () => { drawing.current = false; };
  return <canvas ref={padRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} className="w-full h-40 rounded-2xl bg-white touch-none cursor-crosshair" data-testid="ob-signature-pad" />;
}

export function StepAgreement({ token, onSaved, onBack }) {
  const [ag, setAg] = useState(null);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("Owner");
  const [agree, setAgree] = useState(false);
  const [signed, setSigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const padRef = useRef(null);

  useEffect(() => { api.get(`/onboard/${token}/agreement`).then(({ data }) => setAg(data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, [token]);
  const clear = () => { const c = padRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setSigned(false); };

  const submit = async () => {
    setLoading(true);
    try {
      const blob = await new Promise((res) => padRef.current.toBlob(res, "image/png"));
      const asset = await uploadPublicAsset(new File([blob], "signature.png", { type: "image/png" }), token, "carrier_onboarding");
      const { data } = await api.post(`/onboard/${token}/sign`, { signer_name: name, signer_title: title, signature_url: asset.url, agree });
      onSaved(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!ag) return <p className="text-white/50">Preparing your agreement…</p>;
  const s = ag.summary;
  return (
    <div>
      <StepTitle kicker="Step 10 · Final Step" title="Review & Sign Your Agreement" sub="Your agreement is generated from the details you provided. Please read it carefully before signing." />
      <div className="grid sm:grid-cols-4 gap-3 mb-5">
        {[["Plan", `${s.plan.percent}% · ${s.plan.tier}`], ["Trucks", s.trucks], ["Add-Ons / Month", money(s.addon_monthly)], ["Payment", "Weekly E-Mandate"]].map(([k, v]) => (
          <div key={k} className="rounded-2xl bg-white/[0.04] border border-white/10 p-4"><p className="text-[11px] uppercase tracking-wider text-white/45">{k}</p><p className="font-heading font-bold text-white mt-1" data-testid={`ob-ag-${k.split(" ")[0].toLowerCase()}`}>{v}</p></div>
        ))}
      </div>
      <div className="rounded-2xl bg-white text-[#111827] px-6 sm:px-12 py-8 sm:py-10 max-h-[520px] overflow-y-auto shadow-[0_20px_60px_-20px_rgba(0,0,0,.6)]" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif" }} data-testid="ob-agreement-doc">
        <div className="flex items-center justify-between gap-4 pb-3 border-b-[3px] border-double border-[#0b1f3a]">
          <img src="/freightx-logo.webp" alt="Asterism Services LLC" className="w-16 h-16 object-contain" />
          <div className="text-right text-[11px] leading-snug text-slate-600">
            <p className="text-[17px] font-bold tracking-wide text-[#0b1f3a]">{(ag.company.legal_name || "").toUpperCase()}</p>
            <p>{ag.company.address}</p><p>EIN: {ag.company.ein || "—"}</p>
            <p>{[ag.company.phone, ag.company.email, ag.company.website].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
        <h3 className="text-center text-xl font-bold tracking-wide text-[#0b1f3a] mt-6" style={{ fontFamily: "inherit" }}>DISPATCH SERVICES AGREEMENT</h3>
        <p className="text-center italic text-[13px] text-slate-500">{ag.title}</p>
        <div className="flex justify-between text-[12px] mt-4"><span><b>Agreement No.:</b> {ag.number}</span><span><b>Effective Date:</b> {ag.effective}</span></div>
        <div className="grid sm:grid-cols-2 border border-slate-300 mt-3 text-[12px]">
          {[["DISPATCHER", [["Legal Name", ag.company.legal_name], ["EIN", ag.company.ein], ["Address", ag.company.address]]],
            ["CARRIER", [["Legal Name", ag.carrier.legal_name], ["USDOT", ag.carrier.usdot], ["MC", ag.carrier.mc], ["EIN", ag.carrier.ein], ["Address", ag.carrier.address]]]].map(([h, rows], i) => (
            <div key={h} className={`p-3 ${i ? "sm:border-l border-t sm:border-t-0 border-slate-300" : ""}`}><p className="text-[10px] font-bold tracking-wider text-slate-500">{h}</p>
              {rows.map(([k, v]) => <p key={k}><b>{k}:</b> {v || "—"}</p>)}</div>))}
        </div>
        <p className="font-bold text-[#0b1f3a] mt-5 text-[14px]">RECITALS</p>
        <p className="text-[13.5px] leading-relaxed text-justify mt-1">{ag.parties}</p>
        {ag.sections.map(([t, body], n) => (
          <div key={t} className="mt-4">
            <p className="font-bold text-[#0b1f3a] text-[13.5px]">ARTICLE {n + 1}. {t.replace("Terms & Conditions: ", "").toUpperCase()}</p>
            <p className="mt-1 text-[13.5px] leading-relaxed text-justify whitespace-pre-line">{body}</p>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <OField label="Full Legal Name *"><input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="ob-signer-name" /></OField>
        <OField label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="ob-signer-title" /></OField>
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5"><span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50">Sign Below *</span><button onClick={clear} className="text-xs text-sky-300 inline-flex items-center gap-1" data-testid="ob-signature-clear"><Eraser size={13} />Clear</button></div>
        <SignaturePad padRef={padRef} onChange={setSigned} />
      </div>
      <label className="mt-5 flex items-start gap-3 text-sm text-white/75 cursor-pointer">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 w-4 h-4 accent-emerald-500" data-testid="ob-agree" />
        I have read and agree to the Dispatch Services Agreement and authorize Asterism Services LLC to collect fees using my selected payment method.
      </label>
      <NavButtons onBack={onBack} onNext={submit} loading={loading} disabled={!name.trim() || !signed || !agree} nextLabel="Sign Agreement" testid="ob-sign-btn" />
    </div>
  );
}
